import "react";
import iconOnDark from "@/assets/brand/icon-on-dark.svg";
import iconOnLight from "@/assets/brand/icon-on-light.svg";
import logoOnDark from "@/assets/brand/logo-on-dark.svg";
import logoOnLight from "@/assets/brand/logo-on-light.svg";
import { cn } from "@/lib/utils";

/**
 * eScribe brand assets (src/assets/*.svg):
 * - logo-on-*.svg: rectangular lockup (icon + wordmark), ~3.7:1
 * - icon-on-*.svg: square tile icon, corner radius baked into the artwork
 * The "-on-light"/"-on-dark" suffix names the surface the variant sits ON.
 */

const ICON_SIZE_CLASSES = {
  sm: "size-6",
  md: "size-8",
  lg: "size-9",
  xl: "size-12",
};

const LOCKUP_SIZE_CLASSES = {
  sm: "h-6",
  md: "h-8",
  lg: "h-9",
  xl: "h-12",
};

/**
 * Renders the light/dark variant pair for the current theme, or a single
 * variant when the surrounding surface is always dark (e.g. the sidebar,
 * which stays navy in both themes).
 */
const ThemedImage = ({ lightSrc, darkSrc, surface, alt, className }) => {
  if (surface === "dark") {
    return <img src={darkSrc} alt={alt} className={className} />;
  }
  return (
    <>
      <img src={lightSrc} alt={alt} className={cn("dark:hidden", className)} />
      <img
        src={darkSrc}
        alt={alt}
        className={cn("hidden dark:block", className)}
      />
    </>
  );
};

/**
 * eScribe Logo Component
 *
 * @param {string} size - Size variant: 'sm' (24px), 'md' (32px), 'lg' (36px), 'xl' (48px)
 * @param {boolean} showText - true renders the full lockup (icon + wordmark);
 *   false renders the square icon only
 * @param {string} text - Accessible name (defaults to "eScribe")
 * @param {string} subtext - Optional small line rendered under the logo
 * @param {string} surface - "auto" follows the theme; "dark" forces the
 *   on-dark variant for permanently dark surfaces
 * @param {string} className - Additional classes for the container
 * @param {string} iconClassName - Additional classes for the image(s)
 */
export const Logo = ({
  size = "md",
  showText = false,
  text = "eScribe",
  subtext,
  surface = "auto",
  className,
  iconClassName,
}) => (
  <div className={cn("flex flex-col items-start gap-1", className)}>
    {showText ? (
      <ThemedImage
        lightSrc={logoOnLight}
        darkSrc={logoOnDark}
        surface={surface}
        alt={text}
        className={cn("w-auto", LOCKUP_SIZE_CLASSES[size], iconClassName)}
      />
    ) : (
      <ThemedImage
        lightSrc={iconOnLight}
        darkSrc={iconOnDark}
        surface={surface}
        alt={text}
        className={cn("shrink-0", ICON_SIZE_CLASSES[size], iconClassName)}
      />
    )}
    {subtext && (
      <span className="truncate text-xs text-muted-foreground">{subtext}</span>
    )}
  </div>
);

/**
 * Logo Icon Only (for compact spaces)
 */
export const LogoIcon = ({ size = "md", surface = "auto", className }) => (
  <ThemedImage
    lightSrc={iconOnLight}
    darkSrc={iconOnDark}
    surface={surface}
    alt="eScribe"
    className={cn("shrink-0", ICON_SIZE_CLASSES[size], className)}
  />
);

export default Logo;
